import { useEffect, useState } from "react";
import { toast } from "sonner";
import { axiosInstance } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Palette, ImagePlus, Trash2, MessageCircle, Smartphone } from "lucide-react";
import { themeStyle, bgStyle } from "../utils/brandTheme";

const PRESETS = ["#f59e0b", "#ef4444", "#10b981", "#0ea5e9", "#6366f1", "#ec4899", "#84cc16", "#0f172a"];

const Preview = ({ s }) => (
  <div className="rounded-xl overflow-hidden border border-slate-200 bg-slate-50" style={{ ...themeStyle(s), ...bgStyle(s) }} data-testid="public-settings-preview">
    <div className="bg-slate-900 px-3 py-2 flex items-center justify-between">
      <span className="text-white text-sm font-black truncate">{s.brand_name || "—"}</span>
      <span className="text-[10px] text-brand font-semibold uppercase">Resultados en vivo</span>
    </div>
    <div className={`m-2 p-2 space-y-2 rounded-lg ${s.bg_image_url ? "bg-white/85 backdrop-blur-md" : ""}`}>
      <div className="rounded-lg bg-slate-900 text-white px-3 py-2 flex flex-wrap items-center justify-between gap-1">
        <span className="text-xs font-black">{s.brand_name}</span>
        <span className="flex gap-1 text-[10px]">
          {s.whatsapp && <span className="rounded-full bg-emerald-600 px-2 py-0.5 flex items-center gap-1"><MessageCircle className="w-3 h-3" />{s.whatsapp}</span>}
          {s.sinpe && <span className="rounded-full bg-white/10 px-2 py-0.5 flex items-center gap-1"><Smartphone className="w-3 h-3 text-brand" />{s.sinpe}</span>}
        </span>
      </div>
      <div className="rounded-lg bg-slate-900 p-2 flex gap-2">
        {["09", "92", "61"].map((n) => (
          <div key={n} className="flex-1 rounded-md bg-white/5 p-1.5 flex flex-col items-center gap-1">
            <span className="w-9 h-9 rounded-full bg-brand-gradient text-on-brand font-black font-mono flex items-center justify-center">{n}</span>
            <span className="text-[9px] font-mono text-brand">19:00</span>
          </div>
        ))}
      </div>
      <span className="inline-block rounded-full bg-brand text-on-brand text-[10px] font-semibold px-2 py-0.5">Costa Rica (4)</span>
    </div>
  </div>
);

export const PublicPageSettings = ({ linkId, settings, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [s, setS] = useState(settings);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setS(settings); }, [open]);
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const validColor = /^#[0-9a-fA-F]{6}$/.test(s.primary_color || "");

  const save = async () => {
    setBusy(true);
    try {
      const r = await axiosInstance.put(`/lottery/public-settings?link_id=${linkId}`, {
        brand_name: s.brand_name.trim(), primary_color: s.primary_color, whatsapp: s.whatsapp.trim(), sinpe: s.sinpe.trim(),
      });
      onSaved(r.data);
      toast.success("Página actualizada. Las pantallas abiertas la verán en su próxima actualización (máx. 2 min).");
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "No se pudo guardar");
    }
    setBusy(false);
  };
  const uploadBg = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axiosInstance.post(`/lottery/public-settings/background?link_id=${linkId}`, fd);
      setS((prev) => ({ ...prev, bg_image_url: r.data.bg_image_url }));
      onSaved({ ...settings, bg_image_url: r.data.bg_image_url });
      toast.success("Imagen de fondo guardada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo subir la imagen");
    }
    setBusy(false);
  };
  const removeBg = async () => {
    await axiosInstance.delete(`/lottery/public-settings/background?link_id=${linkId}`);
    setS((prev) => ({ ...prev, bg_image_url: null }));
    onSaved({ ...settings, bg_image_url: null });
  };

  return (
    <>
      <Button size="sm" variant="outline" className="h-9 gap-1" onClick={() => setOpen(true)} data-testid="public-settings-button"><Palette className="w-4 h-4" /> Personalizar página</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="public-settings-dialog">
          <DialogHeader>
            <DialogTitle>Personalizar página pública</DialogTitle>
            <DialogDescription>Se aplica a la página pública y al Modo TV.</DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="space-y-3">
              <div>
                <Label>Nombre del negocio</Label>
                <Input value={s.brand_name} onChange={set("brand_name")} maxLength={40} className="mt-1" data-testid="public-settings-name" />
              </div>
              <div>
                <Label>Color principal</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="color" value={validColor ? s.primary_color : "#f59e0b"} onChange={set("primary_color")} className="w-11 h-10 rounded border border-slate-200 cursor-pointer" data-testid="public-settings-color" />
                  <Input value={s.primary_color} onChange={set("primary_color")} maxLength={7} className="font-mono w-28" data-testid="public-settings-color-hex" />
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {PRESETS.map((c) => (
                    <button key={c} onClick={() => setS({ ...s, primary_color: c })} title={c} data-testid={`public-settings-preset-${c.slice(1)}`}
                      className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${s.primary_color === c ? "border-slate-900" : "border-white shadow"}`} style={{ background: c }} />
                  ))}
                </div>
              </div>
              <div>
                <Label>Imagen de fondo</Label>
                <div className="flex items-center gap-2 mt-1">
                  <label className="flex-1 flex items-center gap-2 h-10 px-3 rounded-md border border-dashed border-slate-300 text-sm text-slate-600 cursor-pointer hover:bg-slate-50">
                    <ImagePlus className="w-4 h-4" /> {s.bg_image_url ? "Cambiar imagen" : "Subir imagen"}
                    <input type="file" accept="image/*" className="hidden" disabled={busy} onChange={(e) => uploadBg(e.target.files?.[0])} data-testid="public-settings-bg" />
                  </label>
                  {s.bg_image_url && <Button size="icon" variant="outline" className="text-red-600" onClick={removeBg} data-testid="public-settings-bg-remove"><Trash2 className="w-4 h-4" /></Button>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>WhatsApp</Label><Input value={s.whatsapp} onChange={set("whatsapp")} maxLength={20} className="mt-1" data-testid="public-settings-whatsapp" /></div>
                <div><Label>Sinpe</Label><Input value={s.sinpe} onChange={set("sinpe")} maxLength={20} className="mt-1" data-testid="public-settings-sinpe" /></div>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Vista previa</Label>
              <Preview s={s} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={busy || !s.brand_name.trim() || !validColor} data-testid="public-settings-save">Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
